import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import { Base_URL } from "../Client/apiURL";
import { openInternalResultTab } from "../utils/openInternalResultTab";

function getAttemptId(attempt) {
  return attempt?.MockTestAttemptId || attempt?.mock_test_attempt_id || attempt?.id || attempt?.MockTestAttemptedId;
}

function getMockTestName(attempt) {
  return attempt?.MockTestName || attempt?.mock_test_name || attempt?.TestName || `Mock attempt ${getAttemptId(attempt) || ""}`;
}

export function getPteRoute(attempt, studentId) {
  const attemptId = getAttemptId(attempt);
  const testType = String(attempt?.TestType || attempt?.test_type || "Full").toLowerCase();
  if (testType === "question type") {
    return `/mt-score-question-type/${studentId}/${attemptId}`;
  }
  const routeType = testType === "full" ? "full" : "sectional";
  const sectionType = routeType === "full" ? "Full" : (attempt?.TestType || attempt?.test_type || "");
  return `/mt-score-${routeType}/${studentId}/${attemptId}/${encodeURIComponent(sectionType)}`;
}

function getResultDateValue(result) {
  const summaryAttempt = Array.isArray(result?.attempts_summary) ? result.attempts_summary[0] : null;
  const nestedAttempt = result?.latest_full_attempt_summary || result?.fullTestAttempt || result?.full_test_attempt || summaryAttempt || {};
  const dateValue =
    result?.DateTaken ||
    result?.dateTaken ||
    result?.completionDate ||
    result?.updated_at ||
    result?.created_at ||
    result?.end_time ||
    result?.start_time ||
    nestedAttempt?.DateTaken ||
    nestedAttempt?.dateTaken ||
    nestedAttempt?.completionDate ||
    nestedAttempt?.updated_at ||
    nestedAttempt?.created_at ||
    nestedAttempt?.end_time ||
    nestedAttempt?.start_time;
  const timestamp = new Date(dateValue || 0).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function sortResultsNewestFirst(results = []) {
  return [...results].sort((a, b) => getResultDateValue(b) - getResultDateValue(a));
}

function isFullResult(result) {
  const testType = String(result?.TestType || result?.test_type || result?.testType || result?.test_type_label || "").toLowerCase();
  return (
    testType === "full" ||
    testType === "full mock test" ||
    Boolean(result?.latest_full_attempt_summary) ||
    Boolean(result?.full_test_attempt_id) ||
    Boolean(result?.fullTestAttemptId) ||
    Boolean(result?.fullTestAttempt?.id) ||
    Boolean(result?.full_test_attempt?.id)
  );
}

export function groupResultsByTestKind(results = []) {
  return sortResultsNewestFirst(results).reduce(
    (groups, result) => {
      const testType = String(result?.TestType || result?.test_type || "").toLowerCase();
      if (testType === "question type") {
        groups.questionType.push(result);
      } else if (isFullResult(result)) {
        groups.full.push(result);
      } else {
        groups.sectional.push(result);
      }
      return groups;
    },
    { full: [], sectional: [], questionType: [] }
  );
}

export function paginateResults(results = [], page = 1, pageSize = 10) {
  const safePageSize = Number(pageSize) > 0 ? Number(pageSize) : 10;
  const totalPages = Math.max(1, Math.ceil(results.length / safePageSize));
  const safePage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
  const startIndex = (safePage - 1) * safePageSize;
  return {
    items: results.slice(startIndex, startIndex + safePageSize),
    totalPages,
  };
}

function getIeltsRoute(test, studentId) {
  const fullAttemptId =
    test?.latest_full_attempt_summary?.full_test_attempt_id ||
    test?.fullTestAttemptId ||
    test?.full_test_attempt_id;
  if (fullAttemptId) {
    return `/ielts-test-details?user=teacher&type=full&studentId=${studentId}&fullTestAttemptId=${fullAttemptId}`;
  }

  const attempt = test?.attempts_summary?.[0] || test?.attempts?.[0] || null;
  const attemptId = attempt?.id || attempt?.attemptId || attempt?.attempt_id || test?.attempt_id;
  if (attemptId) {
    return `/ielts-test-details?user=teacher&type=individual&studentId=${studentId}&attemptId=${attemptId}`;
  }

  const groupId = test?.groupId || test?.group_id || test?.groupid;
  if (groupId) {
    return `/ielts-test-details?user=teacher&type=group&studentId=${studentId}&groupId=${groupId}`;
  }

  return null;
}

function getIeltsScore(test) {
  const attempt = test?.latest_full_attempt_summary || test?.attempts_summary?.[0] || test?.attempts?.[0] || {};
  return attempt?.totalMarksObtained || attempt?.total_marks_obtained || attempt?.overallBandScore || attempt?.score || "-";
}

const sectionTitleSx = { fontWeight: 900, color: "#241B3A", mb: 1 };
const testKindOptions = [
  { value: "full", label: "Full Tests" },
  { value: "sectional", label: "Sectional Tests" },
  { value: "questionType", label: "Question Type" },
];
const pageSizeOptions = [10, 25, 50];

const TeacherStudentResults = () => {
  const { studentId, resultType } = useParams();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeKind, setActiveKind] = useState("full");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const normalizedType = String(resultType || "").toLowerCase();

  useEffect(() => {
    const fetchOverview = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${Base_URL}/app/users/teacher-access/students/${studentId}/overview`,
          { withCredentials: true }
        );
        if (response.data.responseCode === 200) {
          setOverview(response.data.response);
        } else {
          setError(response.data.message || "Could not load student results.");
        }
      } catch (requestError) {
        setError("Could not load student results.");
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [studentId]);

  const ieltsTests = useMemo(() => groupResultsByTestKind(overview?.ielts?.response?.tests || []), [overview]);
  const pteAttempts = useMemo(() => groupResultsByTestKind(overview?.pteAttempts || []), [overview]);
  const activeGroups = normalizedType === "pte" ? pteAttempts : ieltsTests;
  const visibleKindOptions = normalizedType === "pte"
    ? testKindOptions
    : testKindOptions.filter((option) => option.value !== "questionType");
  const selectedResults = activeGroups[activeKind] || [];
  const { items: pagedResults, totalPages } = useMemo(
    () => paginateResults(selectedResults, page, pageSize),
    [selectedResults, page, pageSize]
  );
  const studentName = overview?.student?.name || `Student ${studentId}`;

  const title = normalizedType === "pte" ? "PTE Results" : "IELTS Results";

  useEffect(() => {
    setPage(1);
    if (normalizedType !== "pte" && activeKind === "questionType") setActiveKind("full");
  }, [activeKind, pageSize, normalizedType, studentId]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <>
      <Navbar />
      <Box sx={{ width: "92%", maxWidth: 1180, mx: "auto", pt: { xs: 11, md: 14 }, pb: 5 }}>
        <Stack spacing={2.5}>
          <Box>
            <Button onClick={() => navigate("/teacher-mode")} sx={{ textTransform: "none", mb: 1 }}>
              Back to Teacher Mode
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 900, color: "#241B3A" }}>
              {title}
            </Typography>
            <Typography sx={{ color: "#6B6478", mt: 0.5 }}>{studentName}</Typography>
          </Box>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && (
            <>
              {activeGroups.full.length === 0 && activeGroups.sectional.length === 0 && activeGroups.questionType.length === 0 && (
                <Alert severity="info">No {normalizedType === "pte" ? "PTE" : "IELTS"} results found for this student.</Alert>
              )}

              {(activeGroups.full.length > 0 || activeGroups.sectional.length > 0 || activeGroups.questionType.length > 0) && (
                <>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {visibleKindOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={activeKind === option.value ? "contained" : "outlined"}
                          onClick={() => setActiveKind(option.value)}
                          sx={{
                            textTransform: "none",
                            borderRadius: 1.5,
                            fontWeight: 800,
                            boxShadow: "none",
                            backgroundColor: activeKind === option.value ? "#6B41CB" : "transparent",
                            borderColor: "#D8CFF0",
                            color: activeKind === option.value ? "#fff" : "#4D3A75",
                            "&:hover": {
                              backgroundColor: activeKind === option.value ? "#5633A8" : "#F8F5FE",
                              borderColor: "#6B41CB",
                              boxShadow: "none",
                            },
                          }}
                        >
                          {option.label} ({activeGroups[option.value]?.length || 0})
                        </Button>
                      ))}
                    </Stack>

                    <FormControl size="small" sx={{ minWidth: 132 }}>
                      <Select
                        value={pageSize}
                        onChange={(event) => setPageSize(Number(event.target.value))}
                        sx={{ borderRadius: 1.5, fontWeight: 700, color: "#4D3A75" }}
                      >
                        {pageSizeOptions.map((size) => (
                          <MenuItem key={size} value={size}>{size} per page</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  {selectedResults.length === 0 ? (
                    <Alert severity="info">
                      No {activeKind === "full" ? "Full" : activeKind === "sectional" ? "Sectional" : "Question Type"} {normalizedType === "pte" ? "PTE" : "IELTS"} results found for this student.
                    </Alert>
                  ) : normalizedType === "ielts" ? (
                    <Box>
                      <Typography variant="h6" sx={sectionTitleSx}>
                        {activeKind === "full" ? "Full Tests" : activeKind === "sectional" ? "Sectional Tests" : "Question Type Tests"}
                      </Typography>
                      <Grid container spacing={2}>
                        {pagedResults.map((test, index) => {
                          const route = getIeltsRoute(test, studentId);
                          return (
                            <Grid item xs={12} md={6} key={`${test.id || test.groupId || test.full_test_attempt_id || index}-${index}`}>
                              <Card sx={{ borderRadius: 2, height: "100%", boxShadow: "0 12px 32px rgba(35,25,66,0.08)" }}>
                                <CardContent>
                                  <Stack spacing={1.2}>
                                    <Typography sx={{ fontWeight: 850 }}>
                                      {test.testName || test.test_name || test.groupName || test.group_name || "IELTS test"}
                                    </Typography>
                                    <Typography sx={{ color: "#6B6478", fontSize: 13 }}>
                                      {test.categoryName || test.category_name || test.testType || test.test_type || "IELTS"}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Chip size="small" label={`Score: ${getIeltsScore(test)}`} />
                                      {test.latest_full_attempt_summary?.status && (
                                        <Chip size="small" variant="outlined" label={test.latest_full_attempt_summary.status} />
                                      )}
                                    </Stack>
                                    <Button variant="contained" disabled={!route} onClick={() => openInternalResultTab(route)}>
                                      Open
                                    </Button>
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="h6" sx={sectionTitleSx}>
                        {activeKind === "full" ? "Full Tests" : activeKind === "sectional" ? "Sectional Tests" : "Question Type Tests"}
                      </Typography>
                      <Stack spacing={1.5}>
                        {pagedResults.map((attempt, index) => {
                          const attemptId = getAttemptId(attempt);
                          return (
                            <Card key={`${attemptId || index}-${index}`} sx={{ borderRadius: 2, boxShadow: "0 12px 32px rgba(35,25,66,0.08)" }}>
                              <CardContent>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 850 }}>{getMockTestName(attempt)}</Typography>
                                    <Typography sx={{ color: "#6B6478", fontSize: 13 }}>
                                      Attempt ID {attemptId || "-"} • {attempt?.TestType || "Full"}
                                    </Typography>
                                  </Box>
                                  <Button variant="contained" disabled={!attemptId} onClick={() => openInternalResultTab(getPteRoute(attempt, studentId))}>
                                    Open
                                  </Button>
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {selectedResults.length > pageSize && (
                    <Stack direction="row" justifyContent="center">
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        shape="rounded"
                      />
                    </Stack>
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default TeacherStudentResults;
